// IndexedDB-backed persistence for tournament history (archival data).
//
// Why: js/state.js used to keep the full archival copy of every past tournament
// (all groups/players/rounds) in a single localStorage key ('tournamentHistory'),
// rewriting the WHOLE array on every save. A handful of multi-group tournaments
// blow the ~5MB localStorage quota and the setItem throw happened mid-save.
// IndexedDB has a much larger quota and lets us write/delete a single entry
// without touching the rest, so it's a straight swap for the history array's
// on-disk representation. The in-memory state.history array is untouched by
// this module — callers still read/write it synchronously exactly as before.
//
// Plain promise-wrapped IDB, no libraries. One DB, one object store, keyed by
// String(tournament id).

const DB_NAME = 'tournamentHistoryDB';
const DB_VERSION = 1;
const STORE_NAME = 'history';

let dbPromise = null;
// Set once IndexedDB has proven unavailable (e.g. private browsing). When true,
// every method below becomes a no-op so callers can keep working in-memory only.
let unavailable = false;

function openDB() {
    if (unavailable) return Promise.resolve(null);
    if (dbPromise) return dbPromise;

    dbPromise = new Promise((resolve) => {
        if (typeof indexedDB === 'undefined') {
            unavailable = true;
            console.warn('IndexedDB is not available in this environment; tournament history will not persist across reloads.');
            resolve(null);
            return;
        }

        let request;
        try {
            request = indexedDB.open(DB_NAME, DB_VERSION);
        } catch (e) {
            unavailable = true;
            console.warn('IndexedDB open threw; tournament history will not persist across reloads.', e);
            resolve(null);
            return;
        }

        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            }
        };

        request.onsuccess = () => {
            const db = request.result;
            db.onversionchange = () => db.close();
            resolve(db);
        };

        request.onerror = () => {
            unavailable = true;
            console.warn('IndexedDB open failed; tournament history will not persist across reloads.', request.error);
            resolve(null);
        };

        request.onblocked = () => {
            unavailable = true;
            console.warn('IndexedDB open blocked; tournament history will not persist across reloads.');
            resolve(null);
        };
    });

    return dbPromise;
}

function withStore(mode, fn) {
    return openDB().then(db => {
        if (!db) return null;
        return new Promise((resolve, reject) => {
            let tx;
            try {
                tx = db.transaction(STORE_NAME, mode);
            } catch (e) {
                reject(e);
                return;
            }
            const store = tx.objectStore(STORE_NAME);
            let result;
            try {
                result = fn(store);
            } catch (e) {
                reject(e);
                return;
            }
            tx.oncomplete = () => resolve(result && result.result !== undefined ? result.result : result);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    });
}

// Read every archival entry currently stored in IDB. Returns [] if IDB is
// unavailable or empty — never throws.
export async function getAllHistoryEntries() {
    try {
        const entries = await withStore('readonly', (store) => {
            return new Promise((resolve, reject) => {
                const req = store.getAll();
                req.onsuccess = () => resolve(req.result || []);
                req.onerror = () => reject(req.error);
            });
        });
        return entries || [];
    } catch (e) {
        console.warn('Failed to read tournament history from IndexedDB:', e);
        return [];
    }
}

// Insert/replace a single archival entry, keyed by String(entry.id).
// Fire-and-forget friendly: resolves false (never throws) on failure.
export async function putHistoryEntry(entry) {
    if (!entry || entry.id === undefined || entry.id === null) return false;
    const record = { ...entry, id: String(entry.id) };
    try {
        await withStore('readwrite', (store) => store.put(record));
        return true;
    } catch (e) {
        console.error('Failed to save tournament history entry to IndexedDB:', e);
        return false;
    }
}

// Bulk-replace the entire store with the given entries (used when history is
// wholesale-replaced, e.g. loaded from a cloud snapshot).
export async function putAllHistoryEntries(entries) {
    if (!Array.isArray(entries)) return false;
    try {
        await withStore('readwrite', (store) => {
            store.clear();
            entries.forEach(entry => {
                if (entry && entry.id !== undefined && entry.id !== null) {
                    store.put({ ...entry, id: String(entry.id) });
                }
            });
        });
        return true;
    } catch (e) {
        console.error('Failed to bulk-save tournament history to IndexedDB:', e);
        return false;
    }
}

// Remove a single archival entry by id. Fire-and-forget friendly.
export async function deleteHistoryEntry(id) {
    if (id === undefined || id === null) return false;
    try {
        await withStore('readwrite', (store) => store.delete(String(id)));
        return true;
    } catch (e) {
        console.error('Failed to delete tournament history entry from IndexedDB:', e);
        return false;
    }
}

// Wipe the entire history object store. Used on explicit logout so a shared
// browser doesn't leak the previous user's archived tournaments to the next
// one. Resolves true on success; resolves false (never throws / rejects) when
// IDB is unavailable or the clear fails, matching the fallback style above.
export async function clearHistoryStore() {
    try {
        await withStore('readwrite', (store) => store.clear());
        return true;
    } catch (e) {
        console.error('Failed to clear tournament history store in IndexedDB:', e);
        return false;
    }
}

// One-time migration: move any existing localStorage 'tournamentHistory' array
// into IDB, then remove the localStorage key. Safe to call every startup — it's
// a no-op once the key is gone. Returns the migrated array (or null if there was
// nothing to migrate / migration wasn't possible).
export async function migrateFromLocalStorage() {
    let raw;
    try {
        raw = localStorage.getItem('tournamentHistory');
    } catch (e) {
        return null;
    }
    if (!raw || raw === 'undefined') return null;

    let entries;
    try {
        entries = JSON.parse(raw);
    } catch (e) {
        console.warn('Could not parse legacy tournamentHistory for migration:', e);
        try { localStorage.removeItem('tournamentHistory'); } catch (e2) { /* ignore */ }
        return null;
    }

    if (!Array.isArray(entries) || entries.length === 0) {
        try { localStorage.removeItem('tournamentHistory'); } catch (e) { /* ignore */ }
        return entries || null;
    }

    // Put each entry individually (per the design) rather than one bulk clear+put,
    // so a partial failure still leaves whatever succeeded in IDB.
    for (const entry of entries) {
        await putHistoryEntry(entry);
    }

    try {
        localStorage.removeItem('tournamentHistory');
    } catch (e) {
        // ignore
    }

    return entries;
}

// Whether IDB has been confirmed unavailable in this session (private browsing
// edge cases, etc). Once true, history persistence is in-memory-only.
export function isHistoryStorageUnavailable() {
    return unavailable;
}
